import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { CalendarDays, GripHorizontal, Mail } from 'lucide-react';

import { crmWebUrl } from '@/features/crm-quick-access/api/crmApi';
import CalendarDrawer from '@/features/crm-quick-access/components/CalendarDrawer';
import MailDrawer from '@/features/crm-quick-access/components/MailDrawer';
import { PINNED_DRAWER_WIDTH } from '@/features/crm-quick-access/components/QuickAccessDrawer';
import { useCalendarFeed, useMailFeed } from '@/features/crm-quick-access/hooks/useQuickAccessFeeds';
import {
	DEFAULT_TOOLBAR_POSITION,
	readDockState,
	readToolbarPosition,
	writeDockState,
	writeToolbarPosition
} from '@/features/crm-quick-access/utils/storage';

const BOTTOM_GAP = 8;
// Keeps the toolbar clear of the top navbar.
const TOP_GAP = 56;
// Pointer travel before a press becomes a drag, so ordinary clicks still open the drawers.
const DRAG_THRESHOLD = 4;

// Keeps the toolbar on screen along its edge.
const clampPosition = (position, element) => {
	const height = element?.offsetHeight ?? 120;
	const maxBottom = Math.max(BOTTOM_GAP, window.innerHeight - height - TOP_GAP);
	return { side: position.side, bottom: Math.min(Math.max(position.bottom, BOTTOM_GAP), maxBottom) };
};

// Below this width a pinned drawer would squeeze the page too far, so drawers stay overlays.
const PIN_MEDIA_QUERY = '(min-width: 1024px)';

const subscribeToPinMedia = (onChange) => {
	const query = window.matchMedia(PIN_MEDIA_QUERY);
	query.addEventListener('change', onChange);
	return () => query.removeEventListener('change', onChange);
};

const readCanPin = () => window.matchMedia(PIN_MEDIA_QUERY).matches;

const badgeLabel = (count) => (count > 99 ? '99+' : String(count));

const LauncherButton = ({ label, badge, badgeTitle, active, onClick, children }) => (
	<button
		type="button"
		className={`crm-qa-launcher${active ? ' crm-qa-launcher--active' : ''}`}
		onClick={onClick}
		aria-label={badge > 0 ? `${label}, ${badgeTitle}` : label}
		title={badge > 0 ? badgeTitle : label}
	>
		{children}
		{badge > 0 && <span className="crm-qa-launcher__badge">{badgeLabel(badge)}</span>}
	</button>
);

/**
 * Floating CRM Mail + Calendar launcher. Each button shows a badge (unread inbox
 * conversations / upcoming events not yet seen) and opens a right-hand overlay
 * drawer with a link through to the full CRM screen. The toolbar stays docked to
 * the left or right edge: dragging slides it along the edge, and crossing the
 * middle of the screen moves it to the other side. Its spot is remembered per browser.
 * A drawer can be pinned, which turns it into a column beside the page (the page
 * narrows to make room) that stays open across reloads and into the CRM.
 */
const CrmQuickAccess = () => {
	const canPin = useSyncExternalStore(subscribeToPinMedia, readCanPin);
	const [pinned, setPinned] = useState(() => readDockState().pinned);
	// Reopen a drawer that was pinned open, but never pop an overlay open on load.
	const [panel, setPanel] = useState(() => (readCanPin() ? readDockState().panel : null));
	const pinnedOpen = pinned && canPin && panel !== null;
	const [position, setPosition] = useState(() => clampPosition(readToolbarPosition(), null));
	const [dragging, setDragging] = useState(false);
	const toolbarRef = useRef(null);
	const positionRef = useRef(position);
	const suppressClickRef = useRef(false);

	const applyPosition = useCallback((next) => {
		positionRef.current = next;
		setPosition(next);
	}, []);

	useEffect(() => {
		const handleResize = () => applyPosition(clampPosition(positionRef.current, toolbarRef.current));
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [applyPosition]);

	useEffect(() => {
		writeDockState({ pinned, panel: pinned ? panel : null });
	}, [panel, pinned]);

	// Narrow the page (see _crm-quick-access.scss) while a pinned drawer takes the right edge.
	useEffect(() => {
		if (!pinnedOpen) return undefined;
		const root = document.documentElement;
		root.setAttribute('data-crm-quick-access-pinned', '');
		root.style.setProperty('--crm-qa-pinned-width', `${PINNED_DRAWER_WIDTH}px`);
		return () => {
			root.removeAttribute('data-crm-quick-access-pinned');
			root.style.removeProperty('--crm-qa-pinned-width');
		};
	}, [pinnedOpen]);

	const mail = useMailFeed(true, panel === 'mail');
	const calendar = useCalendarFeed(true);
	const { markAllSeen } = calendar;

	// Opening the calendar clears its badge, including events that arrive while it is open.
	useEffect(() => {
		if (panel === 'calendar') markAllSeen();
	}, [markAllSeen, panel]);

	const openPanel = (next) => {
		// A pinned drawer stays clickable beside the toolbar, so its button toggles it.
		if (pinnedOpen && panel === next) {
			setPanel(null);
			return;
		}
		setPanel(next);
		if (next === 'calendar') calendar.refresh();
		else mail.refresh(true);
	};

	const closePanel = useCallback(() => setPanel(null), []);

	// The CRM is a separate app, so this is a full page load (like the CRM menu item).
	// A pinned drawer reopens there from the shared dock state.
	const openCrm = useCallback(
		(path) => {
			if (!(pinned && canPin)) setPanel(null);
			window.location.assign(crmWebUrl(path));
		},
		[canPin, pinned]
	);

	const dock = { pinned, canPin, onPinnedChange: setPinned };

	// Moves are tracked on the window from the moment of the press: a quick flick
	// can leave the toolbar before it counts as a drag, so element-level
	// listeners (or pointer capture taken at that point) would miss it.
	const handlePointerDown = (event) => {
		if (event.button !== 0) return;
		// A drag that ended off the toolbar produced no click to swallow.
		suppressClickRef.current = false;
		const { pointerId, clientX: startX, clientY: startY } = event;
		const origin = positionRef.current;
		let moved = false;

		const handleMove = (moveEvent) => {
			if (moveEvent.pointerId !== pointerId) return;
			const dx = moveEvent.clientX - startX;
			const dy = moveEvent.clientY - startY;
			if (!moved) {
				if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
				moved = true;
				setDragging(true);
			}
			const side = moveEvent.clientX < window.innerWidth / 2 ? 'left' : 'right';
			applyPosition(clampPosition({ side, bottom: origin.bottom - dy }, toolbarRef.current));
		};
		const handleEnd = (endEvent) => {
			if (endEvent.pointerId !== pointerId) return;
			window.removeEventListener('pointermove', handleMove);
			window.removeEventListener('pointerup', handleEnd);
			window.removeEventListener('pointercancel', handleEnd);
			if (!moved) return;
			// The release after a drag must not also open a drawer.
			suppressClickRef.current = true;
			setDragging(false);
			writeToolbarPosition(positionRef.current);
		};
		window.addEventListener('pointermove', handleMove);
		window.addEventListener('pointerup', handleEnd);
		window.addEventListener('pointercancel', handleEnd);
	};

	const resetPosition = () => {
		writeToolbarPosition(null);
		applyPosition(clampPosition(DEFAULT_TOOLBAR_POSITION, toolbarRef.current));
	};

	const mailBadge = mail.unreadCount;
	const calendarBadge = calendar.unseenCount;

	return (
		<>
			<div
				ref={toolbarRef}
				role="toolbar"
				aria-label="CRM mail and meetings"
				className={`crm-qa-toolbar crm-qa-toolbar--${position.side}${dragging ? ' crm-qa-toolbar--dragging' : ''}`}
				// Docked against the pinned drawer's edge rather than under it.
				style={{
					bottom: position.bottom,
					...(pinnedOpen && position.side === 'right' ? { right: PINNED_DRAWER_WIDTH } : {})
				}}
				onPointerDown={handlePointerDown}
				onClickCapture={(event) => {
					if (!suppressClickRef.current) return;
					suppressClickRef.current = false;
					event.preventDefault();
					event.stopPropagation();
				}}
			>
				<span
					className="crm-qa-toolbar__grip"
					title="Drag to move along the edge · double-click to reset"
					onDoubleClick={resetPosition}
				>
					<GripHorizontal size={14} />
				</span>
				<LauncherButton
					label="Mail"
					badge={mailBadge}
					badgeTitle={`${mailBadge} unread in Inbox`}
					active={panel === 'mail'}
					onClick={() => openPanel('mail')}
				>
					<Mail size={20} />
				</LauncherButton>
				<LauncherButton
					label="Meetings"
					badge={calendarBadge}
					badgeTitle={`${calendarBadge} new upcoming meeting${calendarBadge === 1 ? '' : 's'}`}
					active={panel === 'calendar'}
					onClick={() => openPanel('calendar')}
				>
					<CalendarDays size={20} />
				</LauncherButton>
			</div>

			<MailDrawer
				open={panel === 'mail'}
				onClose={closePanel}
				dock={dock}
				feed={mail}
				openCrm={openCrm}
			/>
			<CalendarDrawer
				open={panel === 'calendar'}
				onClose={closePanel}
				dock={dock}
				feed={calendar}
				openCrm={openCrm}
			/>
		</>
	);
};

export default CrmQuickAccess;
